import { useQuery } from '@tanstack/react-query';

import { stripeRepo } from '@ctct/domain-layer';
import { stripeApi } from '@ctct/infrastructure-layer';

import { QueryKeys } from './constants';
import { useAchMicroDepositsGate } from '../experiments/useAchMicroDepositsGate';

type SharedStripeElementsCredentialsArgs = {
  countryCode: string | null;
  currencyCode: string | null;
  paymentMethodId: string | null;
  enabled: boolean;
};

type BillingStripeElementsCredentialsArgs = SharedStripeElementsCredentialsArgs & {
  useCase: 'billing';
  quoteId: string | null;
};

type PaymentsOnlyStripeElementsCredentialsArgs = SharedStripeElementsCredentialsArgs & {
  useCase: 'payments-only';
  quoteId?: never;
};

export type StripeElementsCredentialsArgs =
  | BillingStripeElementsCredentialsArgs
  | PaymentsOnlyStripeElementsCredentialsArgs;

const canFetchElementsCredentials = ({
  enabled,
  useCase,
  quoteId,
  countryCode,
  currencyCode,
}: StripeElementsCredentialsArgs) =>
  enabled && Boolean(countryCode) && Boolean(currencyCode) && (useCase === 'payments-only' || Boolean(quoteId));

const createElementsCredentialsPayload = ({
  useCase,
  countryCode,
  currencyCode,
  paymentMethodId,
  quoteId,
  paymentMethodOptions,
}: StripeElementsCredentialsArgs & { paymentMethodOptions: ReturnType<typeof useAchMicroDepositsGate>['data'] }) => {
  const returnUrl = stripeApi.getReturnUrl();
  const sharedPayload = {
    countryCode,
    currencyCode,
    paymentMethodId: typeof paymentMethodId === 'string' ? paymentMethodId : undefined,
    returnUrl: typeof returnUrl === 'string' ? returnUrl : undefined,
    paymentMethodOptions,
  };

  if (useCase === 'payments-only') {
    return { useCase, ...sharedPayload };
  }

  if (!quoteId) {
    throw new Error('Stripe quote ID is required for billing credentials');
  }

  return { useCase, quoteId, ...sharedPayload };
};

export const useStripeElementsCredentials = (args: StripeElementsCredentialsArgs) => {
  const { data: paymentMethodOptions } = useAchMicroDepositsGate();

  return useQuery({
    // Note: stripeQuoteId is deliberately excluded from the query key, since it is only used to identify
    // the customer. If we re-fetch the credentials when we get a new quote, it will discard the customer's
    // entered payment method if not saved.
    // Also: countryCode and currencyCode are also deliberately excluded from the query key, since we want to use them
    // in the initial call but not re-fetch when they change. (To handle changes, we use the useUpdateSetupIntentParams
    // mutation instead.)
    queryKey: [QueryKeys.STRIPE_ELEMENTS_CREDENTIALS],
    queryFn: () =>
      stripeRepo.createElementsCredentials(createElementsCredentialsPayload({ ...args, paymentMethodOptions })),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: canFetchElementsCredentials(args),
  });
};

export type StripeSetupIntentQuery = ReturnType<typeof useStripeElementsCredentials>;
